import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, ShieldAlert, GraduationCap, Award, FileText, Calendar, User } from 'lucide-react';
import SEO from '@/components/common/SEO';

// ─── Design tokens ────────────────────────────────────────────────────────────
const UNI_MIDNIGHT = '#06060c';
const UNI_DARK = '#0a0a14';
const UNI_GOLD = '#d4a843';
const UNI_INDIGO = '#0ea5e9';
const UNI_BORDER = 'rgba(255,255,255,0.07)';
const UNI_CARD = 'rgba(255,255,255,0.04)';
const UNI_TEXT = 'rgba(255,255,255,0.88)';
const UNI_MUTED = 'rgba(255,255,255,0.45)';

interface VerifiedCert {
    id: string;
    recipient_name: string;
    subject_name: string;
    cert_type: string;
    issued_at: string;
    verification_code: string;
    track: 'full' | 'essay_free';
    source: 'degree' | 'prep';
}

function Loader() {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100vh', background: UNI_MIDNIGHT, flexDirection: 'column', gap: 20,
        }}>
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: `3px solid rgba(212,168,67,0.15)`, borderTopColor: UNI_GOLD,
                }}
            />
            <p style={{ color: UNI_MUTED, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
                Verifying credential…
            </p>
        </div>
    );
}

export default function VerifyPage() {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [cert, setCert] = useState<VerifiedCert | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!code) {
            setError('No verification code provided.');
            setLoading(false);
            return;
        }

        async function verify() {
            setLoading(true);
            try {
                // 1. Search prep_certificates
                const { data: prep, error: prepErr } = await supabase
                    .from('prep_certificates')
                    .select(`
            id, subject_name, prep_tier, issued_at, verification_code, certificate_track,
            users (name)
          `)
                    .eq('verification_code', code?.toUpperCase())
                    .maybeSingle();

                if (prep) {
                    setCert({
                        id: prep.id,
                        recipient_name: (prep.users as any)?.name || 'Student',
                        subject_name: prep.subject_name,
                        cert_type: `prep_${prep.prep_tier}`,
                        issued_at: prep.issued_at,
                        verification_code: prep.verification_code,
                        track: prep.certificate_track as any,
                        source: 'prep'
                    });
                    return;
                }

                // 2. Search university_certificates
                const { data: uni, error: uniErr } = await supabase
                    .from('university_certificates')
                    .select(`
            id, subject_name, cert_type, issued_at, verification_code, certificate_track, recipient_name,
            users (name)
          `)
                    .eq('verification_code', code?.toUpperCase())
                    .maybeSingle();

                if (uni) {
                    setCert({
                        id: uni.id,
                        recipient_name: uni.recipient_name || (uni.users as any)?.name || 'Student',
                        subject_name: uni.subject_name || 'Degree Subject',
                        cert_type: uni.cert_type,
                        issued_at: uni.issued_at,
                        verification_code: uni.verification_code,
                        track: uni.certificate_track as any,
                        source: 'degree'
                    });
                    return;
                }

                setError('Credential not found or invalid verification code.');
            } catch (err) {
                console.error('Verification error:', err);
                setError('A system error occurred during verification.');
            } finally {
                setLoading(false);
            }
        }

        verify();
    }, [code]);

    if (loading) return <Loader />;

    return (
        <div style={{
            background: UNI_MIDNIGHT,
            minHeight: '100vh',
            color: UNI_TEXT,
            fontFamily: "'Inter', sans-serif",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '60px 24px',
        }}>
            <SEO title="Verify Credential" noindex />
            {/* Background Decor */}
            <div style={{
                position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)',
                width: 600, height: 600, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(14,165,233,0.05) 0%, transparent 70%)',
                pointerEvents: 'none', zIndex: 0,
            }} />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                    maxWidth: 540, width: '100%', position: 'relative', zIndex: 1,
                    background: UNI_DARK, border: `1px solid ${UNI_BORDER}`, borderRadius: 24,
                    padding: '40px 32px', textAlign: 'center',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                }}
            >
                {cert ? (
                    <>
                        <motion.div
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            style={{
                                width: 80, height: 80, borderRadius: '50%', background: 'rgba(74,222,128,0.1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 24px', color: '#4ade80', border: '1px solid rgba(74,222,128,0.2)',
                            }}
                        >
                            <CheckCircle2 size={44} />
                        </motion.div>

                        <h1 style={{
                            fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 800,
                            margin: '0 0 8px', color: '#fff'
                        }}>
                            Credential Verified
                        </h1>
                        <p style={{ color: UNI_MUTED, fontSize: 15, marginBottom: 32 }}>
                            Still Waters University formally confirms the validity of this academic record.
                        </p>

                        {/* Details Card */}
                        <div style={{
                            background: UNI_CARD, border: `1px solid ${UNI_BORDER}`, borderRadius: 16,
                            padding: '24px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 16,
                            marginBottom: 32,
                        }}>
                            <div style={{ display: 'flex', gap: 14 }}>
                                <User size={18} style={{ color: UNI_GOLD, marginTop: 2 }} />
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: UNI_GOLD, letterSpacing: '1px', marginBottom: 2 }}>STUDENT</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{cert.recipient_name}</div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 14 }}>
                                <Award size={18} style={{ color: UNI_INDIGO, marginTop: 2 }} />
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: UNI_INDIGO, letterSpacing: '1px', marginBottom: 2 }}>CREDENTIAL</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{cert.subject_name}</div>
                                    <div style={{ fontSize: 12, color: UNI_MUTED, marginTop: 2 }}>
                                        {cert.cert_type === 'degree' ? 'Full Degree' : cert.cert_type === 'module' ? 'Module Completion' : cert.cert_type === 'prep_foundation' ? 'Prep (Foundation)' : 'Prep (Bridge)'}
                                        {cert.track === 'essay_free' && ' • Essay-Free'}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 14 }}>
                                <Calendar size={18} style={{ color: UNI_MUTED, marginTop: 2 }} />
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: UNI_MUTED, letterSpacing: '1px', marginBottom: 2 }}>ISSUED ON</div>
                                    <div style={{ fontSize: 14, color: UNI_TEXT }}>{new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 14 }}>
                                <FileText size={18} style={{ color: UNI_MUTED, marginTop: 2 }} />
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: UNI_MUTED, letterSpacing: '1px', marginBottom: 2 }}>VERIFICATION CODE</div>
                                    <div style={{ fontSize: 14, color: UNI_TEXT, fontFamily: 'monospace', letterSpacing: '1px' }}>{cert.verification_code}</div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('/')}
                            style={{
                                width: '100%', background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                                color: '#06060c', border: 'none', borderRadius: 12, padding: '14px',
                                fontSize: 15, fontWeight: 800, cursor: 'pointer',
                            }}
                        >
                            Visit Still Waters University →
                        </button>
                    </>
                ) : (
                    <>
                        <div style={{
                            width: 80, height: 80, borderRadius: '50%', background: 'rgba(239,68,68,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 24px', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)',
                        }}>
                            <ShieldAlert size={44} />
                        </div>

                        <h1 style={{
                            fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 800,
                            margin: '0 0 12px', color: '#fff'
                        }}>
                            Verification Failed
                        </h1>
                        <p style={{ color: '#ef4444', fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
                            {error || 'Invalid Verification Code'}
                        </p>
                        <p style={{ color: UNI_MUTED, fontSize: 14, lineHeight: 1.6, marginBottom: 32 }}>
                            The code provided does not match any academic record in our secure registry.
                            Please check the code and try again.
                        </p>

                        <button
                            onClick={() => navigate('/')}
                            style={{
                                width: '100%', background: 'rgba(255,255,255,0.06)',
                                color: '#fff', border: `1px solid ${UNI_BORDER}`, borderRadius: 12, padding: '14px',
                                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            Back to Home
                        </button>
                    </>
                )}
            </motion.div>

            {/* Footer Branding */}
            <div style={{ marginTop: 40, textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: UNI_MUTED, letterSpacing: '1px', fontWeight: 600 }}>
                    STILL WATERS UNIVERSITY ACADEMIC REGISTRY
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 8 }}>
                    Secure Blockchain-anchored Metadata Protection • 2026
                </p>
            </div>
        </div>
    );
}
